import { useMemo, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';

// R (#153) — one shared search + filter bar for the ten list pages that
// lacked one. Cases already has server-side search/filter/pagination (R3);
// this is the client-side v1 for the smaller pre-launch datasets that every
// listed page already holds in memory. Server-side filtering + pagination is
// deferred to a post-launch phase 2 (explicitly out of scope here).
//
// The bar itself is presentational + controlled. Each page owns its state and
// filtering predicate via `useSearchFilter`, because row shapes differ across
// pages; the shared piece is the UX (text query + facet dropdowns), matching
// Cases' existing input styling so the portal reads uniformly.

/** One facet dropdown: a labelled <select> over a fixed option list. */
export interface FacetSpec<T> {
  /** Stable key; also the state key. */
  id: string;
  /** Visible label / accessible name (used as the "all" placeholder). */
  label: string;
  /** Options in display order. The empty-string value is the reset/"all". */
  options: { value: string; label: string }[];
  /**
   * Whether a row matches the currently-selected value. Never called for the
   * empty value (which always matches everything).
   */
  predicate: (row: T, value: string) => boolean;
}

export interface SearchFilterBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  placeholder?: string;
  /** Facet dropdowns, each with its current value + change handler. */
  facets?: {
    id: string;
    label: string;
    value: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
  }[];
  /** "Showing X of Y" hint, shown when a query or facet is active. */
  matchCount?: number;
  totalCount?: number;
  /** Accessible name for the text input (defaults to the placeholder). */
  searchAriaLabel?: string;
}

export function SearchFilterBar({
  query,
  onQueryChange,
  placeholder = 'Search…',
  facets = [],
  matchCount,
  totalCount,
  searchAriaLabel,
}: SearchFilterBarProps) {
  const anyFacetActive = facets.some((f) => f.value !== '');
  const active = query.trim() !== '' || anyFacetActive;
  const showCount =
    active && typeof matchCount === 'number' && typeof totalCount === 'number';

  const clear = () => {
    onQueryChange('');
    facets.forEach((f) => f.onChange(''));
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <input
        aria-label={searchAriaLabel ?? placeholder}
        style={{ ...styles.input, flex: '1 1 260px', maxWidth: 400, marginBottom: 0 }}
        placeholder={placeholder}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
      />
      {facets.map((f) => (
        <select
          key={f.id}
          aria-label={f.label}
          style={{ ...styles.select, padding: '10px 12px' }}
          value={f.value}
          onChange={(e) => f.onChange(e.target.value)}
        >
          {f.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ))}
      {active && (
        <button
          type="button"
          style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
          onClick={clear}
        >Clear</button>
      )}
      {showCount && (
        <span style={{ fontSize: 12, color: C.textMuted }}>
          Showing {matchCount} of {totalCount}
        </span>
      )}
    </div>
  );
}

export interface UseSearchFilterResult<T> {
  /** Rows passing the current text query + all active facets. */
  filtered: T[];
  /** Whether any filter (text or facet) is currently narrowing the list. */
  active: boolean;
  /** Props to spread onto `<SearchFilterBar {...bar} />`. */
  bar: SearchFilterBarProps;
}

/**
 * State + filtering for a list page. Owns the query string and each facet's
 * selected value, and returns the filtered rows plus the props for the bar.
 *
 * `searchText` maps a row to the space that the free-text query matches
 * against (case-insensitive substring). Facets AND together; within the text
 * query, all whitespace-separated terms must match.
 */
export function useSearchFilter<T>(
  items: T[],
  searchText: (row: T) => string,
  facets: FacetSpec<T>[] = [],
  opts: { placeholder?: string; searchAriaLabel?: string } = {},
): UseSearchFilterResult<T> {
  const [query, setQuery] = useState('');
  const [facetValues, setFacetValues] = useState<Record<string, string>>({});

  const filtered = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter((row) => {
      if (terms.length) {
        const hay = searchText(row).toLowerCase();
        if (!terms.every((t) => hay.includes(t))) return false;
      }
      for (const f of facets) {
        const v = facetValues[f.id] ?? '';
        if (v !== '' && !f.predicate(row, v)) return false;
      }
      return true;
    });
    // searchText / facet predicates are defined inline per render; the row
    // data + query + facet values are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query, facetValues, facets]);

  const anyFacetActive = facets.some((f) => (facetValues[f.id] ?? '') !== '');
  const active = query.trim() !== '' || anyFacetActive;

  const bar: SearchFilterBarProps = {
    query,
    onQueryChange: setQuery,
    placeholder: opts.placeholder,
    searchAriaLabel: opts.searchAriaLabel,
    facets: facets.map((f) => ({
      id: f.id,
      label: f.label,
      value: facetValues[f.id] ?? '',
      options: f.options,
      onChange: (value: string) => setFacetValues((prev) => ({ ...prev, [f.id]: value })),
    })),
    matchCount: filtered.length,
    totalCount: items.length,
  };

  return { filtered, active, bar };
}
